import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { ErrorComponent, ProgressSpinner } from '@medical/components'
import {
  DOCTORS_FIELDS,
  STAFF_BLOCK_DOCTOR,
  STAFF_CREATE_DOCTOR,
  STAFF_RESTORE_BLOCKED_DOCTOR,
  STAFF_UPDATE_DOCTOR,
  STAFF_UPLOAD_DOCTOR_CSV,
  STAFF_UPLOAD_DOCTOR_CSV_UPDATE,
} from '@medical/constant/permissions'
import {
  checkStaffPermission,
  clinicalDepartmentNames,
  findNameOrEmail,
  onPageChange,
} from '@medical/libs'
import Auth from '@medical/middleware/auth'
import { CREATE_ACTIVITY } from '@medical/pages/doctor/AvailableShift/AvailableShift.graphql'
import { useCustom } from '@medical/provider/context'
import { SocketContext } from '@medical/provider/socket'
import {
  removeProgress,
  setProgressBar,
} from '@medical/provider/store/reducers/progress/progress.action'
import queryString from 'query-string'
import React, { useContext, useEffect } from 'react'
import { connect } from 'react-redux'

import {
  BLOCK_DOCTOR,
  DOCTORS_CONNECTION,
  RESTORE_BLOCKED_DOCTOR,
} from './DoctorsList.graphql'
import DoctorListScene from './DoctorsListScene'

const restore = 'restore'
const block = 'block'

const DoctorsList = ({
  history,
  location: { search },
  progress,
  setProgress,
  removeProgress,
}) => {
  const {
    page = 1,
    rowsPerPage = 20,
    orderBy = 'createdAt_DESC',
    fullname,
    email,
    phoneNumber,
  } = queryString.parse(search)
  const first = parseInt(rowsPerPage, 10)
  const skip = (parseInt(page, 10) - 1) * parseInt(rowsPerPage, 10)
  const variables = {
    first: first < 0 ? 20 : first,
    skip: skip < 0 ? 0 : skip,
    orderBy,
    where: {
      AND: findNameOrEmail({ fullname, email }),
      registrationStatus_not_in: ['PENDING', 'REJECTED', 'NEW'],
      phoneNumber_contains: phoneNumber
        ? phoneNumber.replace(/-/g, '')
        : undefined,
    },
  }
  const socket = useContext(SocketContext)
  const [{ i18n, popup }] = useCustom()
  const client = useApolloClient()
  const isDoctorsFieldsPermitted = checkStaffPermission({
    functionName: DOCTORS_FIELDS,
  })
  const isUpdatePermitted = checkStaffPermission({
    functionName: STAFF_UPDATE_DOCTOR,
  })
  const isBlockPermitted = checkStaffPermission({
    functionName: STAFF_BLOCK_DOCTOR,
  })
  const isRestorePermitted = checkStaffPermission({
    functionName: STAFF_RESTORE_BLOCKED_DOCTOR,
  })
  const isCreatePermitted = checkStaffPermission({
    functionName: STAFF_CREATE_DOCTOR,
  })
  const isUploadPermitted = checkStaffPermission({
    functionName: STAFF_UPLOAD_DOCTOR_CSV,
  })
  const isUploadUpdatePermitted = checkStaffPermission({
    functionName: STAFF_UPLOAD_DOCTOR_CSV_UPDATE,
  })
  const [staffCreateActivity] = useMutation(CREATE_ACTIVITY)
  useEffect(() => {
    staffCreateActivity({
      variables: {
        activity: 'STAFF_DISPLAYED_DOCTOR_LIST',
      },
    })
  }, [staffCreateActivity])

  const mutationOptions = {
    refetchQueries: [
      {
        query: DOCTORS_CONNECTION,
        variables,
      },
    ],
    awaitRefetchQueries: true,
  }
  const [blockDoctor] = useMutation(BLOCK_DOCTOR, mutationOptions)
  const [restoreBlockedDoctor] = useMutation(
    RESTORE_BLOCKED_DOCTOR,
    mutationOptions
  )
  const { loading, error, data } = useQuery(DOCTORS_CONNECTION, {
    variables,
    fetchPolicy: 'network-only',
  })

  if (loading) return <ProgressSpinner />
  if (error) return <ErrorComponent error={error} />

  const departmentNames = clinicalDepartmentNames({
    departments: data.departments,
  })
  const token = Auth.getToken()

  const url = `${process.env.REACT_APP_ENDPOINT}/downloadcsv?token=${token}`

  const onSubmit = async (
    { doctorId, functionName },
    { setSubmitting, resetForm }
  ) => {
    try {
      if (functionName === block) {
        await blockDoctor({
          variables: {
            doctorId,
          },
        })
        await staffCreateActivity({
          variables: {
            activity: 'STAFF_BLOCKED_DOCTOR',
            doctorId,
          },
        })
        setSubmitting(false)
        resetForm()
        popup.success(i18n.t('staff.blockDoctor.submissionSuccess'))
      } else {
        await restoreBlockedDoctor({
          variables: {
            doctorId,
          },
        })
        await staffCreateActivity({
          variables: {
            activity: 'STAFF_UNBLOCKED_DOCTOR',
            doctorId,
          },
        })
        setSubmitting(false)
        resetForm()
        popup.success(i18n.t('staff.restoreBlockedDoctor.submissionSuccess'))
      }
    } catch (error) {
      popup.error(error)
      setSubmitting(false)
    }
  }

  const {
    doctorsConnection: {
      edges,
      aggregate: { count },
    },
  } = data
  return (
    <DoctorListScene
      i18n={i18n}
      socket={socket}
      progressList={progress}
      setProgress={progress => setProgress(progress)}
      removeProgress={progress => removeProgress(progress)}
      download={url}
      onSubmit={onSubmit}
      edges={edges}
      count={count}
      variables={variables}
      page={parseInt(page, 10)}
      rowsPerPage={parseInt(rowsPerPage, 10)}
      fullname={fullname}
      email={email}
      phoneNumber={phoneNumber}
      onPageChange={onPageChange(history, search)}
      block={block}
      restore={restore}
      isDoctorsFieldsPermitted={isDoctorsFieldsPermitted}
      isUpdatePermitted={isUpdatePermitted}
      isBlockPermitted={isBlockPermitted}
      isRestorePermitted={isRestorePermitted}
      isCreatePermitted={isCreatePermitted}
      departmentNames={departmentNames}
      isUploadPermitted={isUploadPermitted}
      isUploadUpdatePermitted={isUploadUpdatePermitted}
    />
  )
}

const mapStateToProps = state => ({
  progress: state.progressBarStore.progress,
})

const mapDispatchToProps = dispatch => ({
  setProgress: progress => dispatch(setProgressBar(progress)),
  removeProgress: progress => dispatch(removeProgress(progress)),
})
export default connect(mapStateToProps, mapDispatchToProps)(DoctorsList)
